import { eq, useLiveQuery } from '@tanstack/react-db';
import { createFileRoute } from '@tanstack/react-router';
import { useTable } from '@tanstack/react-table';
import { useMemo, useState } from 'react';

import { AdjustStockDialog } from '@/components/catalog/adjust-stock-dialog';

import {
  DataTable,
  DataTablePagination,
  dataTableColumnHelper,
  dataTableDefaultColumn,
  dataTableFeatures,
} from '@/components/data-table';
import { ProductThumbnail } from '@/components/product-thumbnail';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/lib/auth';
import {
  type InventoryLevelRecord,
  inventoryLevelsCollection,
  type LocationRecord,
  locationsCollection,
  type ProductRecord,
  productsCollection,
  type VariantRecord,
  variantsCollection,
} from '@/lib/collections';

/**
 * Stock on hand, per variant per location.
 *
 * The row is an `inventory_levels` record; everything readable about it —
 * which product, which variant, which location — comes from three other
 * collections joined in memory. That is the shape the table is for: the
 * numbers live in one narrow table and the names live elsewhere.
 *
 * `available` is a generated column (`on_hand - reserved`), so it is displayed
 * and never edited. Adjusting stock is a movement, not an edit of a total —
 * that screen writes `stock_movements` and is a later phase.
 */
export const Route = createFileRoute('/inventory/')({
  loader: async () => {
    await Promise.all([
      inventoryLevelsCollection.preload(),
      variantsCollection.preload(),
      productsCollection.preload(),
      locationsCollection.preload(),
    ]).catch(() => undefined);
  },
  component: InventoryPage,
});

interface InventoryRow {
  id: number;
  product: string;
  variant: string;
  sku: string;
  location: string;
  locationId: string;
  onHand: number;
  reserved: number;
  available: number;
  threshold: number | null;
  /** The product's first image, for the same thumbnail the catalog uses. */
  imageUrl: string | null;
}

/** PostgREST sends `numeric` as a string so precision survives the wire. */
function toNumber(value: number | string | null): number {
  if (value === null || value === undefined) return 0;

  return typeof value === 'number' ? value : Number.parseFloat(value);
}

const column = dataTableColumnHelper<InventoryRow>();

const columns = column.columns([
  column.accessor('product', {
    header: 'Product',
    cell: ({ row }) => (
      <div className="flex items-center gap-3">
        <ProductThumbnail src={row.original.imageUrl} alt={row.original.product} />

        {/* `min-w-0` so long catalog names truncate rather than widening the
            column and pushing the numbers off-screen. */}
        <div className="min-w-0">
          <div className="truncate font-medium">{row.original.product}</div>
          {row.original.sku && (
            <div className="text-muted-foreground truncate text-xs">{row.original.sku}</div>
          )}
        </div>
      </div>
    ),
  }),

  column.accessor('variant', {
    header: 'Variant',
    cell: ({ getValue }) => getValue() || <span className="text-muted-foreground">—</span>,
  }),

  column.accessor('location', {
    header: 'Location',
    cell: ({ getValue }) => getValue() || <span className="text-muted-foreground">—</span>,
  }),

  column.accessor('onHand', {
    header: 'On hand',
    meta: { className: 'text-right tabular-nums' },
    cell: ({ getValue }) => formatQuantity(getValue()),
  }),

  column.accessor('reserved', {
    header: 'Reserved',
    meta: { className: 'text-right tabular-nums' },
    cell: ({ getValue }) => (
      <span className={getValue() > 0 ? '' : 'text-muted-foreground'}>
        {formatQuantity(getValue())}
      </span>
    ),
  }),

  column.accessor('available', {
    header: 'Available',
    meta: { className: 'text-right tabular-nums' },
    cell: ({ row }) => {
      const { available, threshold } = row.original;

      /*
       * Three states, not two. "Out" and "low" need different responses — one
       * is a lost sale happening now, the other is a reorder — so they should
       * not look the same.
       */
      if (available <= 0) {
        return <Badge variant="destructive">Out</Badge>;
      }

      if (threshold !== null && available <= threshold) {
        return (
          <span className="font-medium">
            {formatQuantity(available)}
            <Badge variant="outline" className="ml-2 font-normal">
              Low
            </Badge>
          </span>
        );
      }

      return <span className="font-medium">{formatQuantity(available)}</span>;
    },
  }),

  column.display({
    id: 'actions',
    header: () => <span className="sr-only">Actions</span>,
    meta: { className: 'text-right' },
    cell: ({ row, table }) =>
      table.options.meta?.canEdit ? (
        <Button
          variant="outline"
          size="sm"
          onClick={() => table.options.meta?.onEdit?.(String(row.original.id))}
        >
          Adjust
        </Button>
      ) : null,
  }),
]);

function formatQuantity(value: number): string {
  // Stock is `numeric` because half-gram units are real; whole numbers should
  // still read as whole numbers.
  return Number.isInteger(value)
    ? value.toLocaleString()
    : value.toLocaleString(undefined, {
        maximumFractionDigits: 3,
      });
}

function InventoryPage() {
  const { activeAccountId, claims } = useAuth();
  const accountId = activeAccountId ?? '';

  /*
   * Adjusting stock is gated on the `inventory` capability rather than a role
   * permission, matching the API. Administering inventory settings and counting
   * what is on the shelf are different jobs.
   */
  const canEdit = accountId
    ? (claims.resourcesByAccount[accountId] ?? []).includes('inventory') ||
      Boolean(claims.permissionsByAccount[accountId]?.includes('members.manage'))
    : false;

  const [globalFilter, setGlobalFilter] = useState('');
  const [locationId, setLocationId] = useState('all');
  const [stock, setStock] = useState<'all' | 'in' | 'low' | 'out'>('all');
  const [adjusting, setAdjusting] = useState<number | null>(null);

  const levels = useLiveQuery(
    (q) =>
      q
        .from({ level: inventoryLevelsCollection })
        .where(({ level }) => eq(level.account_id, accountId)),
    [accountId],
  );

  const variants = useLiveQuery(
    (q) =>
      q
        .from({ variant: variantsCollection })
        .where(({ variant }) => eq(variant.account_id, accountId)),
    [accountId],
  );

  const products = useLiveQuery(
    (q) =>
      q
        .from({ product: productsCollection })
        .where(({ product }) => eq(product.account_id, accountId)),
    [accountId],
  );

  const locations = useLiveQuery(
    (q) =>
      q
        .from({ location: locationsCollection })
        .where(({ location }) => eq(location.account_id, accountId)),
    [accountId],
  );

  const locationList = (locations.data ?? []) as LocationRecord[];

  const rows = useMemo<InventoryRow[]>(() => {
    const variantById = new Map(
      ((variants.data ?? []) as VariantRecord[]).map((variant) => [variant.id, variant]),
    );
    const productById = new Map(
      ((products.data ?? []) as ProductRecord[]).map((product) => [product.id, product]),
    );
    const locationById = new Map(locationList.map((location) => [location.id, location.name]));

    return ((levels.data ?? []) as InventoryLevelRecord[]).map((level) => {
      const variant = variantById.get(level.variant_id);
      const product = variant ? productById.get(variant.product_id) : undefined;
      const threshold = level.low_stock_threshold;

      return {
        id: level.id,
        // A level whose variant has not synced yet still has to render as
        // something an operator can recognise as incomplete.
        product: product?.name ?? 'Unknown product',
        variant: variant?.name ?? '',
        sku: variant?.sku ?? '',
        location: locationById.get(level.location_id) ?? '',
        locationId: level.location_id,
        onHand: toNumber(level.on_hand),
        reserved: toNumber(level.reserved),
        available: toNumber(level.available),
        threshold: threshold === null || threshold === undefined ? null : toNumber(threshold),
        imageUrl: product?.image_urls?.[0] ?? null,
      };
    });
  }, [levels.data, variants.data, products.data, locationList]);

  const scoped = useMemo(() => {
    return rows.filter((row) => {
      if (locationId !== 'all' && row.locationId !== locationId) return false;

      if (stock === 'out') return row.available <= 0;
      if (stock === 'in') return row.available > 0;
      if (stock === 'low') {
        return row.available > 0 && row.threshold !== null && row.available <= row.threshold;
      }

      return true;
    });
  }, [rows, locationId, stock]);

  const totals = useMemo(
    () => ({
      onHand: scoped.reduce((sum, row) => sum + row.onHand, 0),
      out: scoped.filter((row) => row.available <= 0).length,
    }),
    [scoped],
  );

  const table = useTable({
    features: dataTableFeatures,
    defaultColumn: dataTableDefaultColumn,
    columns,
    data: scoped,
    meta: { canEdit, onEdit: (id: string) => setAdjusting(Number(id)) },
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter,
    initialState: {
      pagination: { pageIndex: 0, pageSize: 25 },
      // Lowest availability first: the rows that need a decision today.
      sorting: [{ id: 'available', desc: false }],
    },
    globalFilterFn: (row, _columnId, value) => {
      const term = String(value ?? '')
        .trim()
        .toLowerCase();

      if (!term) return true;

      const { product, variant, sku, location } = row.original;

      return (
        product.toLowerCase().includes(term) ||
        variant.toLowerCase().includes(term) ||
        sku.toLowerCase().includes(term) ||
        location.toLowerCase().includes(term)
      );
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Inventory</h1>
          <p className="text-muted-foreground text-sm">
            {levels.isLoading
              ? 'Syncing stock…'
              : `${formatQuantity(totals.onHand)} units on hand across ${scoped.length.toLocaleString()} line${scoped.length === 1 ? '' : 's'}${totals.out ? ` · ${totals.out} out of stock` : ''}`}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* One location is the common case, so the picker only earns its
              place once there is more than one to pick. */}
          {locationList.length > 1 && (
            <Select value={locationId} onValueChange={setLocationId}>
              <SelectTrigger className="w-44" aria-label="Filter by location">
                <SelectValue />
              </SelectTrigger>

              <SelectContent>
                <SelectItem value="all">All locations</SelectItem>
                {locationList.map((location) => (
                  <SelectItem key={location.id} value={location.id}>
                    {location.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Select value={stock} onValueChange={(value) => setStock(value as typeof stock)}>
            <SelectTrigger className="w-36" aria-label="Filter by stock level">
              <SelectValue />
            </SelectTrigger>

            <SelectContent>
              <SelectItem value="all">All stock</SelectItem>
              <SelectItem value="in">In stock</SelectItem>
              <SelectItem value="low">Low stock</SelectItem>
              <SelectItem value="out">Out of stock</SelectItem>
            </SelectContent>
          </Select>

          <Input
            placeholder="Filter by product, SKU or location…"
            value={globalFilter}
            onChange={(event) => setGlobalFilter(event.target.value)}
            className="w-72"
          />
        </div>
      </div>

      <DataTable
        table={table}
        columnCount={columns.length}
        isLoading={levels.isLoading}
        // Opening the adjustment dialog *is* the detail view here, so a row is
        // only a target for somebody who may adjust stock.
        onRowActivate={canEdit ? (row) => setAdjusting(row.id) : undefined}
        emptyMessage={
          rows.length
            ? 'No stock lines match those filters.'
            : 'No stock recorded yet. Receiving a purchase order or a Metrc package creates these.'
        }
      />

      <DataTablePagination table={table} total={rows.length} />

      {adjusting !== null &&
        (() => {
          const level = rows.find((row) => row.id === adjusting);

          return level ? (
            <AdjustStockDialog
              key={adjusting}
              open
              onOpenChange={(next) => !next && setAdjusting(null)}
              level={level}
            />
          ) : null;
        })()}
    </div>
  );
}
